import moment from "moment";

import {
  API_DATE_FORMAT,
  DISPLAY_DATE_FORMAT,
  DISPLAY_DATE_WITH_TIME_FORMAT,
  DYNAMIC_DATE_OPTIONS,
  ORDER_TYPE_RETURN
} from "./consts";

import { Address, OrderParty } from "@/models/api/orders/get";

import { AxiosResponse } from "axios";
import { StockSkuResource } from "./models/api/barcodeSystem/stockSkus/stockSkus";
import permissions from "./security/permissions";

const formatDate = (value, format = null) => {
  if (format === null) format = DISPLAY_DATE_FORMAT;

  return value === null || value === undefined ? "" : moment(value, API_DATE_FORMAT).format(format);
};

const formatDateWithTime = value => {
  return value === null ? "" : moment(value).format(DISPLAY_DATE_WITH_TIME_FORMAT);
};

const formatDecimal = (value, decimalPlaces = 2) => {
  return value === null || value === undefined ? "" : value.toFixed(decimalPlaces);
};

const formatUoMQuantity = (value: number, stockSku: StockSkuResource = null) => {
  if(value == 0 ||!stockSku || !stockSku.uoMs)
    return value.toString();

  var baseUoMAbbreviation = stockSku.baseUoM ? stockSku.baseUoM.abbreviation : '';
  var quantityString = '';
  
  if(stockSku.defaultUoM){
    var defaultQty = Math.floor(value/stockSku.defaultUoM.baseConversionFactor);
    if(defaultQty > 0) {
      quantityString = defaultQty.toString() + ' ' + stockSku.defaultUoM.abbreviation;
      var baseQty = value - (defaultQty * stockSku.defaultUoM.baseConversionFactor);
      if(baseQty > 0) {
        quantityString += ', ' + baseQty.toString() + ' ' + baseUoMAbbreviation;
      }
      return quantityString + ' (' + value + ' ' + baseUoMAbbreviation + ')'
    }
  } 

  return value.toString() + ' ' + baseUoMAbbreviation;
}

const formatDynamicDate = value => {
  let from = startDateFromDynamicRange(value);
  let to = endDateFromDynamicRange(value);

  const range = from.format("MM/DD/YYYY") + " ~ " + to.format("MM/DD/YYYY");

  return range;
};

const startDateFromDynamicRange = value => {
  let from = moment();

  if (value === "yesterday") {
    from = moment().subtract(1, "days");
  } else if (value === "last week") {
    from = moment().subtract(7, "days");
  } else if (value === "14 days ago") {
    from = moment().subtract(14, "days");
  } else if (value === "last month") {
    from = moment().subtract(1, "months");
  } else if (value === "last 3 months") {
    from = moment().subtract(3, "months");
  } else if (value === "last 6 months") {
    from = moment().subtract(6, "months");
  } else if (value === "tomorrow") {
    from = moment().add(1, "days");
  } else if (value === "next week") {
    from = moment(); // Start from today for "Next 7 Days"
  } else if (value === "next month") {
    from = moment(); // Start from today for "Next 30 Days"
  }

  return from;
};

const endDateFromDynamicRange = value => {
  let to = moment();

  if (value === "yesterday") {
    to = moment().subtract(1, "days");
  } else if (value === "last week") {
    // Empty on purpose - end date is today for "Last 7 Days"
  } else if (value === "14 days ago") {
    to = moment().subtract(14, "days");
  } else if (value === "last month") {
    // Empty on purpose - end date is today for "Last 30 Days"
  } else if (value === "tomorrow") {
    to = moment().add(1, "days");
  } else if (value === "next week") {
    to = moment().add(7, "days"); // End 7 days from now for "Next 7 Days"
  } else if (value === "next month") {
    to = moment().add(30, "days"); // End 30 days from now for "Next 30 Days"
  }

  return to;
};

const isDynamicDateRange = value => {
  var option = DYNAMIC_DATE_OPTIONS.filter(x => x.value === value);
  return option != null && option.length > 0;
};

const formatBool = value => {
  if (value == null) return "";

  return value ? "YES" : "NO";
};

const formatZip = zips => {
  return zips.join(", ");
};

const formatTruckTemplateZip = zips => {
  return zips.map(zip => zip.zip3).join(", ");
};

const formatTruckTemplateWeekdays = weekdays => {
  let arr = [
    { text: "Monday", value: 2 },
    { text: "Tuesday", value: 3 },
    { text: "Wednesday", value: 4 },
    { text: "Thursday", value: 5 },
    { text: "Friday", value: 6 }
  ];
  return weekdays.map(weekday => arr.find(x => x.value === weekday.weekDay).text).join(", ");
};

const formatPhoneNumber = (number: string) => {
  var cleaned = ("" + number).replace(/\D/g, "");
  var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return "(" + match[1] + ") " + match[2] + "-" + match[3];
  }
  return null;
};

const formatAddress = (address: Address) => {
  if (address) {
    var formatedString = address.streetLine1 + "\n";
    if (address.streetLine2 && address.streetLine2 != "") {
      formatedString = formatedString + address.streetLine2 + "\n";
    }
    formatedString = formatedString + address.city + ", " + address.state + " " + address.zip;

    return formatedString;
  }
  return null;
};

const formatOrderParty = (orderParty: OrderParty, includePhone: boolean = false) => {
  if (orderParty) {
    var formatedString = orderParty.name + "\n" + formatAddress(orderParty.address);
    if (includePhone) {
      if (orderParty.primaryPhone && orderParty.primaryPhone != "") {
        formatedString = formatedString + "\n" + formatPhoneNumber(orderParty.primaryPhone);
      }
      if (orderParty.secondaryPhone && orderParty.secondaryPhone != "") {
        formatedString = formatedString + "\n" + formatPhoneNumber(orderParty.secondaryPhone);
      }
    }

    return formatedString;
  }
  return null;
};


const formatInventorySummaryType = value => {
  switch(value)
  {
    case "I":
      return "ITEM";
    case "P":
      return "PALLET";
    case "B":
      return "BULK";
  }
  return "";
};

const getOrdersMapLocation = orders => {
  let minLat = 180,
    maxLat = -180,
    minLng = 180,
    maxLng = -180,
    latCenter = null,
    lngCenter = null,
    zoomLevel = null;

  orders.forEach(order => {
    let orderLocation = getOrderLocation(order);
    if (orderLocation) {
      maxLat = maxLat < orderLocation.latitude ? orderLocation.latitude : maxLat;
      minLat = minLat > orderLocation.latitude ? orderLocation.latitude : minLat;
      maxLng = maxLng < orderLocation.longitude ? orderLocation.longitude : maxLng;
      minLng = minLng > orderLocation.longitude ? orderLocation.longitude : minLng;
    }
  });
  let newLatCenter = (minLat + maxLat) / 2;
  let newLngCenter = (minLng + maxLng) / 2;
  let maxDegree = maxLat - minLat > maxLng - minLng ? maxLat - minLat : maxLng - minLng;
  if (newLatCenter !== 0 && newLngCenter !== 0) {
    latCenter = newLatCenter;
    lngCenter = newLngCenter;
  }
  if (maxDegree != -360) {
    let newZoomLevel = Math.abs(Math.floor(Math.log2(1 / (180 / maxDegree))));
    zoomLevel = newZoomLevel > 17 ? 17 : newZoomLevel;
  }
  return {
    latCenter: latCenter,
    lngCenter: lngCenter,
    zoomLevel: zoomLevel
  };
};

const getOrderLocation = order => {
  if (order) {
    if (order.location) return order.location;

    const locationParty = getOrderLocationParty(order);
    if (locationParty && locationParty.location) return locationParty.location;
  }

  return null;
};

const getOrderLocationParty = order => {
  if (order) {
    if (order.orderType == ORDER_TYPE_RETURN && order.shipper) {
      return order.shipper;
    } else if (order.consignee) {
      return order.consignee;
    }
  }
  return null;
};

const getFileName = (response: AxiosResponse): string => {
  var filename = "";

  var disposition = response.headers["content-disposition"];

  if (disposition && disposition.indexOf("filename") !== -1) {
    var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    var matches = filenameRegex.exec(disposition);
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, "");
    }
  }
  return filename;
};

const permissionExists = (permission: string, node = null): boolean => {
  if(node == null)
    node = permissions;

  if (typeof node === "string")
  {
    if(node === permission)
    {
      return true;
    }
    return false;
  }

  if (typeof node === "object" && node !== null) {
    for (let child in node)
    {
      if(permissionExists(permission, node[child]))
        return true;
    }
  }
   
  return false;
}




export {
  getOrdersMapLocation,
  formatDate,
  formatZip,
  formatTruckTemplateWeekdays,
  formatTruckTemplateZip,
  formatDateWithTime,
  formatDynamicDate,
  formatBool,
  formatPhoneNumber,
  formatAddress,
  formatOrderParty,
  formatInventorySummaryType,
  getFileName,
  formatDecimal,
  startDateFromDynamicRange,
  endDateFromDynamicRange,
  isDynamicDateRange,
  getOrderLocation,
  getOrderLocationParty,
  formatUoMQuantity,
  permissionExists
};
